import React, { useState, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, MessageCircle, Clock, GitBranch, ShieldCheck, ArrowRight, Star } from 'lucide-react';
// --- Components ---
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
const Button = ({
  children,
  variant = 'primary',
  className = '',
  href,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'bg-[#25D366] text-white hover:bg-[#20bd5a] shadow-lg shadow-green-200 focus:ring-[#25D366]',
    secondary: 'bg-white text-gray-800 border-2 border-gray-200 hover:border-[#25D366] hover:text-[#25D366] focus:ring-gray-200'
  };
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={`${baseStyles} ${variants[variant]} ${className}`} data-id="element-1">
        {children}
      </a>;
  }
  return <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props} data-id="element-2">
      {children}
    </button>;
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-4 md:px-8 ${className}`} data-id="element-3">
    <div className="max-w-4xl mx-auto" data-id="element-4">{children}</div>
  </section>;
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => <AnimatePresence data-id="element-5">
    {isOpen && <>
        <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-6" />
        <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      type: 'spring',
      duration: 0.5
    }} className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col" data-id="element-7">
          <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-8">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-9">{title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Cerrar" data-id="element-10">
              <X className="w-6 h-6 text-gray-500" data-id="element-11" />
            </button>
          </div>
          <div className="overflow-y-auto p-6 flex-1" data-id="element-12">{children}</div>
        </motion.div>
      </>}
  </AnimatePresence>;
// --- Main App ---
export function App() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen font-sans text-gray-800 bg-[#FDF8F3]" data-id="element-13">
      {/* 1. HERO SECTION */}
      {/* 1. HERO SECTION */}
      <header className="relative overflow-hidden bg-white pt-6 pb-10 sm:pt-8 sm:pb-12 lg:pt-24 lg:pb-32" data-id="element-14">
        <div className="max-w-6xl mx-auto px-4 md:px-8" data-id="element-15">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-16">
            <FadeIn data-id="element-17">
              <div className="space-y-4 md:space-y-6" data-id="element-18">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs sm:text-sm font-medium" data-id="element-19">
                  <Star className="w-4 h-4 mr-2 fill-current" data-id="element-20" />
                  Para vender sin sentirte 'vendedora'
                </div>

                {/* H1 compacta para mobile */}
                <h1 className="font-bold tracking-tight text-gray-900 leading-[1.08] text-[28px] sm:text-4xl md:text-5xl lg:text-6xl" data-id="element-21">
                  ¿Se te van ventas en{' '}
                  <span className="block text-[#25D366] inline" data-id="element-22">WhatsApp </span>{' '}
                  por no saber qué responder?
                  <span className="block text-[#25D366]" data-id="element-23"></span>
                </h1>

                {/* Subhead 1 línea/2 líneas máximo */}
                <p className="text[-15px] sm:text-lg text-gray-600 leading-snug" data-id="element-24">
                  Plan de 7 dias con{' '}
                  <span className="block text-[#25D366] font-medium inline" data-id="element-25">
                    Guiones listos{' '}
                  </span>{' '}
                  para responder, manejar objeciones y cerrar con calma (sin
                  sonar insistente).
                </p>

                <div className="pt-1" data-id="element-26">
                  <Button onClick={scrollToPricing} className="w-full sm:w-auto !px-6 !py-3 !text-base" data-id="element-27">
                    Empezar mi Plan
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-28" />
                  </Button>

                  {/* Microcopy que explica que baja al precio */}
                  {/*<p className="mt-2 text-xs sm:text-sm text-gray-500">
                   Baja para ver precio + lo que incluye ↓
                   </p>*/}

                  <p className="mt-2 text-xs sm:text-sm text-gray-500 flex items-center" data-id="element-29">
                    <ShieldCheck className="w-4 h-4 mr-2 text-green-600" data-id="element-30" />
                    Garantía de claridad 7 días
                  </p>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-31">
              <div className="relative mx-auto w-full max-w-md lg:max-w-full" data-id="element-32">
                <div className="absolute -inset-3 bg-[#25D366]/10 rounded-3xl rotate-2 blur-lg" data-id="element-33"></div>

                <img src="https://res.cloudinary.com/dab9nhevk/image/upload/f_auto,q_auto:good,w_700/heylloyd-cover-image-ver3c_tl03ot.jpg" alt="Organiza tus mensajes de WhatsApp en 7 días - ebook" className="relative rounded-2xl shadow-2xl w-full border border-gray-100 max-h-[420px] sm:max-h-none object-contain" loading="eager" data-id="element-34" />
              </div>
            </FadeIn>
          </div>
        </div>
      </header>

      {/* 2. OPENING LEAD */}
      <Section className="bg-[#FDF8F3]" data-id="element-35">
        <FadeIn data-id="element-36">
          <div className="prose prose-lg mx-auto text-gray-700" data-id="element-37">
            <p className="text-2xl font-medium text-gray-900 mb-8 leading-relaxed" data-id="element-38">
              Hay un tipo de cansancio que no se dice en voz alta:
            </p>
            <p className="text-xl font-bold text-gray-800 mb-8 border-l-4 border-[#25D366] pl-6 italic" data-id="element-39">
              Sabes que podrías vender… pero cuando toca escribir el mensaje, te
              quedas mirando la pantalla.
            </p>
            <p className="mb-6" data-id="element-40">
              No porque no sepas de tu trabajo. No porque tu producto sea malo.
            </p>
            <p className="mb-8" data-id="element-41">
              Sino porque WhatsApp te exige algo que nadie te enseñó:{' '}
              <strong data-id="element-42">conducir una conversación con estructura</strong>.
            </p>
            <p className="mb-6" data-id="element-43">
              Y cuando no hay estructura, pasa lo mismo siempre:
            </p>
            <ul className="space-y-4 list-none pl-0 mb-8" data-id="element-44">
              {['Escribes párrafos (y te dejan en visto)', 'Te da pena insistir (y se enfría)', 'Te preguntan el precio (y lo dices con culpa)', 'Terminas trabajando más… por menos'].map((item, i) => <li key={i} className="flex items-start" data-id="element-45">
                  <X className="w-5 h-5 text-red-400 mr-3 mt-1 flex-shrink-0" data-id="element-46" />
                  <span data-id="element-47">{item}</span>
                </li>)}
            </ul>
            <p className="text-lg font-medium" data-id="element-48">
              Este ebook no intenta "motivarte". Te da un sistema.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* 3. PROBLEM SECTION */}
      <Section className="bg-white" data-id="element-49">
        <FadeIn data-id="element-50">
          <div className="text-center mb-12" data-id="element-51">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-52">
              No te falta seguridad. <br className="hidden md:block" data-id="element-53" />
              Te falta un guión.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-54">
            <div data-id="element-55">
              <p className="text-lg text-gray-700 mb-6" data-id="element-56">
                Si vender por WhatsApp te da vergüenza, no es un defecto de
                personalidad. <strong data-id="element-57">Es un problema de proceso.</strong>
              </p>
              <p className="text-lg text-gray-700 mb-6" data-id="element-58">
                Porque WhatsApp se siente personal. Y eso lo vuelve incómodo
                cuando no tienes claro:
              </p>
              <ul className="space-y-3 mb-6" data-id="element-59">
                {['Cómo abrir una conversación sin molestar', 'Qué preguntar para que la otra persona se entienda', 'Cómo presentar tu oferta sin sonar a anuncio', 'Qué contestar cuando aparece el clásico: "lo pienso"'].map((item, i) => <li key={i} className="flex items-start text-gray-700" data-id="element-60">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#25D366] mt-2.5 mr-3 flex-shrink-0" data-id="element-61" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 shadow-sm" data-id="element-62">
              <h3 className="font-bold text-xl mb-4 text-gray-900" data-id="element-63">
                El costo de improvisar:
              </h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-64">
                {[{
                icon: MessageCircle,
                text: 'Silencios incómodos'
              }, {
                icon: X,
                text: 'Vistos sin respuesta'
              }, {
                icon: Star,
                text: 'Descuentos por miedo'
              }, {
                icon: Clock,
                text: 'Desgaste mental'
              }].map((item, i) => <div key={i} className="bg-white p-4 rounded-xl text-center shadow-sm border border-gray-100 flex flex-col items-center justify-center gap-2" data-id="element-65">
                    <item.icon className="w-8 h-8 text-gray-400" data-id="element-66" />
                    <span className="font-medium text-gray-700" data-id="element-67">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 4. WHAT THEY'VE TRIED */}
      <Section className="bg-[#FDF8F3]" data-id="element-68">
        <FadeIn data-id="element-69">
          <div className="max-w-3xl mx-auto text-center" data-id="element-70">
            <h2 className="text-3xl font-bold text-gray-900 mb-12" data-id="element-71">
              Lo que la mayoría hace{' '}
              <span className="text-gray-500 font-normal" data-id="element-72">
                (y por qué no funciona)
              </span>
            </h2>

            <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-73">
              {[{
              title: '“Sé constante”',
              desc: 'Pero no te dicen qué escribir exactamente.'
            }, {
              title: '“Haz contenido”',
              desc: 'Pero no te dicen cómo convertir likes en chats.'
            }, {
              title: '“Cree en ti”',
              desc: 'Pero la duda no se resuelve con frases, sino con estructura.'
            }].map((card, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-74">
                  <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-75">
                    {card.title}
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-76">{card.desc}</p>
                </div>)}
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md border-l-4 border-red-400 inline-block w-full max-w-2xl" data-id="element-77">
              <p className="font-medium text-gray-500 uppercase tracking-wide text-xs mb-2" data-id="element-78">
                El ciclo sin fin
              </p>
              <div className="flex flex-wrap justify-center items-center gap-3 text-lg font-medium text-gray-800" data-id="element-79">
                <span data-id="element-80">No escribes</span>
                <ArrowRight className="w-5 h-5 text-gray-400" data-id="element-81" />
                <span data-id="element-82">No vendes</span>
                <ArrowRight className="w-5 h-5 text-gray-400" data-id="element-83" />
                <span data-id="element-84">Te frustras</span>
                <ArrowRight className="w-5 h-5 text-gray-400" data-id="element-85" />
                <span className="text-red-500" data-id="element-86">Repites</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 5. THE SOLUTION */}
      <Section className="bg-white" data-id="element-87">
        <FadeIn data-id="element-88">
          <div className="text-center mb-16" data-id="element-89">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-90">
              La solución real es más simple <br className="hidden md:block" data-id="element-91" />
              (y menos emocional)
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-92">
              Necesitas 3 piezas para vender sin sentirte "vendedora".
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-93">
            {[{
            icon: MessageCircle,
            title: 'Guiones Modulares',
            desc: 'Mensajes por bloques que combinas según el caso. No memorizas. No inventas. Eliges.',
            color: 'bg-blue-50 text-blue-600'
          }, {
            icon: Clock,
            title: 'Rutina de 12 minutos',
            desc: 'Un hábito mínimo diario para abrir, seguir y cerrar. Usar el celular con intención.',
            color: 'bg-green-50 text-green-600'
          }, {
            icon: GitBranch,
            title: 'Árbol de Respuestas',
            desc: 'Un mapa para objeciones: "caro", "luego", "no tengo tiempo". Sigues la rama correcta.',
            color: 'bg-purple-50 text-purple-600'
          }].map((card, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-94">
                <div className={`w-14 h-14 rounded-2xl ${card.color} flex items-center justify-center mb-6`} data-id="element-95">
                  <card.icon className="w-7 h-7" data-id="element-96" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-97">
                  {card.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-98">{card.desc}</p>
              </div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 6. WHAT'S INCLUDED */}
      <Section className="bg-[#FDF8F3]" data-id="element-99">
        <FadeIn data-id="element-100">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-101">
            <div className="grid md:grid-cols-2" data-id="element-102">
              <div className="p-8 md:p-12" data-id="element-103">
                <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-104">
                  Qué incluye (sin relleno)
                </h2>
                <ul className="space-y-4" data-id="element-105">
                  {['Cómo definir tu oferta en 1 frase', 'Ajuste rápido de WhatsApp (perfil, bio, evidencias)', 'Guiones listos para abrir conversación', 'Secuencia de diagnóstico en chat', 'Cómo decir el precio con estructura', 'Seguimientos que no suenan intensos', 'Árbol de respuestas para objeciones', 'Plan día por día (7 días)'].map((item, i) => <li key={i} className="flex items-start" data-id="element-106">
                      <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0" data-id="element-107">
                        <Check className="w-4 h-4 text-[#25D366]" data-id="element-108" />
                      </div>
                      <span className="text-gray-700 font-medium" data-id="element-109">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div className="bg-gray-50 p-8 md:p-12 flex items-center justify-center border-t md:border-t-0 md:border-l border-gray-100" data-id="element-110">
                <div className="text-center" data-id="element-111">
                  <div className="inline-block p-4 bg-white rounded-2xl shadow-lg mb-6 transform rotate-2" data-id="element-112">
                    <img src="https://res.cloudinary.com/dab9nhevk/image/upload/f_auto,q_auto:good,w_700/heylloyd-cover-image-ver3c_tl03ot.jpg" alt="Book Cover" className="w-48 h-auto rounded-lg" data-id="element-113" />
                  </div>
                  <p className="text-gray-500 text-sm" data-id="element-114">
                    Formato Digital (PDF) + Recursos
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 7. WHO IT'S FOR */}
      <Section className="bg-white" data-id="element-115">
        <FadeIn data-id="element-116">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16" data-id="element-117">
            <div data-id="element-118">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-119">
                <span className="bg-green-100 text-green-700 p-2 rounded-lg mr-3" data-id="element-120">
                  <Check className="w-6 h-6" data-id="element-121" />
                </span>
                Esto es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-122">
                {['Vendes servicios, asesorías o productos y quieres usar WhatsApp como canal principal', 'Te da pena vender pero sí estás dispuesta a enviar mensajes', 'Quieres un método simple que se sienta humano y respetuoso'].map((item, i) => <li key={i} className="flex items-start text-gray-700" data-id="element-123">
                    <Check className="w-5 h-5 text-[#25D366] mr-3 mt-1 flex-shrink-0" data-id="element-124" />
                    <span data-id="element-125">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div data-id="element-126">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-127">
                <span className="bg-red-100 text-red-700 p-2 rounded-lg mr-3" data-id="element-128">
                  <X className="w-6 h-6" data-id="element-129" />
                </span>
                No es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-130">
                {['Buscas "dinero sin hablar con nadie"', 'No tienes ninguna oferta (ni una habilidad concreta)', 'Quieres copiar mensajes agresivos que no se parecen a ti'].map((item, i) => <li key={i} className="flex items-start text-gray-700" data-id="element-131">
                    <X className="w-5 h-5 text-red-400 mr-3 mt-1 flex-shrink-0" data-id="element-132" />
                    <span data-id="element-133">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 8. RESULTS */}
      <Section className="bg-[#FDF8F3]" data-id="element-134">
        <FadeIn data-id="element-135">
          <div className="text-center mb-12" data-id="element-136">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-137">
              Resultados que puedes esperar <br className="hidden md:block" data-id="element-138" />
              (sin prometerte magia)
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-139">
              En 7 días, lo más probable es que no te conviertas en otra
              persona. Pero sí vas a notar algo más útil:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-140">
            {[{
            text: 'Calma frente al chat',
            icon: '😌'
          }, {
            text: 'Sabes qué enviar',
            icon: '📝'
          }, {
            text: 'Dejas de regalar tiempo',
            icon: '⏳'
          }, {
            text: 'Guías hacia la decisión',
            icon: '🎯'
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl text-center shadow-sm border border-gray-100" data-id="element-141">
                <div className="text-4xl mb-4" data-id="element-142">{item.icon}</div>
                <p className="font-bold text-gray-800" data-id="element-143">{item.text}</p>
              </div>)}
          </div>
        </FadeIn>
      </Section>

      {/* 9. GUARANTEE */}
      <Section className="bg-white" data-id="element-144">
        <FadeIn data-id="element-145">
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-200 text-center" data-id="element-146">
            <ShieldCheck className="w-16 h-16 text-[#25D366] mx-auto mb-6" data-id="element-147" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-148">
              Garantía de claridad (7 días)
            </h3>
            <p className="text-lg text-gray-600 mb-0" data-id="element-149">
              Si haces el Plan de 7 días y aún así sientes que "no sabes qué
              decir", te devuelvo el dinero. Sin preguntas incómodas.
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* 10. FINAL CTA */}
      <section id="pricing" className="py-20 md:py-32 px-4 bg-[#111827] text-white text-center" data-id="element-150">
        <FadeIn data-id="element-151">
          <div className="max-w-4xl mx-auto" data-id="element-152">
            <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-153">
              Si quieres vender sin actuar como alguien que no eres: <br data-id="element-154" />
              <span className="text-[#25D366]" data-id="element-155">este sistema es para ti.</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto" data-id="element-156">
              Hoy mismo manda tus 3 primeros mensajes con estructura.
            </p>

            <div className="flex flex-col items-center space-y-6" data-id="element-157">
              <Button href="https://www.ixanstudio.com/cart/50481561600247:1" className="w-full sm:w-auto text-xl px-12 py-5 shadow-[0_0_30px_rgba(37,211,102,0.4)]" data-id="element-158">
                Descargar "Sistema Ventas por Whatsapp"
                <ArrowRight className="ml-2 w-6 h-6" data-id="element-159" />
              </Button>

              <div className="flex items-center justify-center gap-3" data-id="element-160">
                <div className="text-center" data-id="element-161">
                  <p className="text-gray-500 line-through text-lg mb-1" data-id="element-162">
                    $36.900
                  </p>
                  <p className="text-4xl font-bold text-[#25D366]" data-id="element-163">$21.690</p>
                </div>
                <div className="bg-[#25D366] text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg" data-id="element-164">
                  41% OFF
                </div>
              </div>

              <div className="flex items-center space-x-2 text-sm text-gray-400" data-id="element-165">
                <ShieldCheck className="w-4 h-4" data-id="element-166" />
                <span data-id="element-167">Pago seguro y acceso inmediato</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer className="bg-[#111827] border-t border-gray-800 py-12 px-4 text-center text-gray-500 text-sm" data-id="element-168">
        <div className="max-w-4xl mx-auto" data-id="element-169">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-4" data-id="element-170">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-400 hover:text-[#25D366] transition-colors underline underline-offset-4" data-id="element-171">
              Política de Privacidad
            </button>
            <span className="hidden sm:inline text-gray-700" data-id="element-172">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-400 hover:text-[#25D366] transition-colors underline underline-offset-4" data-id="element-173">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-174">
            © {new Date().getFullYear()} Ventas por WhatsApp. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-175">
        <div className="prose prose-sm max-w-none text-gray-700" data-id="element-176">
          <p className="text-gray-500 text-sm mb-6" data-id="element-177">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-178">
            1. Información que Recopilamos
          </h3>
          <p data-id="element-179">
            Al adquirir nuestro ebook "Ventas por WhatsApp en 7 Días",
            recopilamos únicamente la información necesaria para procesar tu
            compra y entregarte el producto digital:
          </p>
          <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-180">
            <li data-id="element-181">Nombre y apellido</li>
            <li data-id="element-182">Dirección de correo electrónico</li>
            <li data-id="element-183">
              Información de pago (procesada de forma segura por nuestro
              proveedor de pagos)
            </li>
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-184">
            2. Uso de tu Información
          </h3>
          <p data-id="element-185">Utilizamos tu información personal para:</p>
          <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-186">
            <li data-id="element-187">Procesar y completar tu compra</li>
            <li data-id="element-188">Enviarte el ebook y recursos relacionados</li>
            <li data-id="element-189">Comunicarnos contigo sobre tu compra</li>
            <li data-id="element-190">
              Enviarte actualizaciones del producto (puedes cancelar la
              suscripción en cualquier momento)
            </li>
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-191">
            3. Protección de Datos
          </h3>
          <p data-id="element-192">
            Implementamos medidas de seguridad para proteger tu información
            personal. No vendemos, intercambiamos ni transferimos tu información
            personal a terceros sin tu consentimiento, excepto cuando sea
            necesario para procesar pagos o cumplir con la ley.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-193">
            4. Cookies
          </h3>
          <p data-id="element-194">
            Nuestro sitio web puede utilizar cookies para mejorar tu experiencia
            de navegación. Puedes configurar tu navegador para rechazar cookies,
            aunque esto puede afectar algunas funcionalidades del sitio.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-195">
            5. Tus Derechos
          </h3>
          <p data-id="element-196">Tienes derecho a:</p>
          <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-197">
            <li data-id="element-198">Acceder a tu información personal</li>
            <li data-id="element-199">Solicitar la corrección de datos inexactos</li>
            <li data-id="element-200">Solicitar la eliminación de tus datos</li>
            <li data-id="element-201">Cancelar la suscripción a nuestras comunicaciones</li>
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-202">
            6. Contacto
          </h3>
          <p data-id="element-203">
            Si tienes preguntas sobre esta Política de Privacidad o deseas
            ejercer tus derechos, contáctanos a través de los canales
            proporcionados en tu compra.
          </p>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-204">
        <div className="prose prose-sm max-w-none text-gray-700" data-id="element-205">
          <p className="text-gray-500 text-sm mb-6" data-id="element-206">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-207">
            1. Aceptación de Términos
          </h3>
          <p data-id="element-208">
            Al comprar y descargar "Ventas por WhatsApp en 7 Días", aceptas
            estos Términos y Condiciones. Si no estás de acuerdo, no adquieras
            ni uses el producto.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-209">
            2. Descripción del Producto
          </h3>
          <p data-id="element-210">
            Este es un producto digital (ebook en formato PDF) que proporciona
            un sistema de guiones, rutinas y respuestas para vender por
            WhatsApp. El producto incluye:
          </p>
          <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-211">
            <li data-id="element-212">Guiones modulares para conversaciones de ventas</li>
            <li data-id="element-213">Rutina diaria de 12 minutos</li>
            <li data-id="element-214">Árbol de respuestas para objeciones</li>
            <li data-id="element-215">Plan de implementación de 7 días</li>
            <li data-id="element-216">Recursos y plantillas complementarias dentro del Ebook.</li>
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-217">
            3. Licencia de Uso
          </h3>
          <p data-id="element-218">
            Al comprar este ebook, recibes una licencia personal, no
            transferible y no exclusiva para usar el contenido. Está prohibido:
          </p>
          <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-219">
            <li data-id="element-220">Compartir, distribuir o revender el ebook</li>
            <li data-id="element-221">Reproducir el contenido sin autorización</li>
            <li data-id="element-222">Modificar o crear trabajos derivados</li>
            <li data-id="element-223">Usar el contenido para fines comerciales sin permiso</li>
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-224">
            4. Garantía de 7 Días
          </h3>
          <p data-id="element-225">
            Ofrecemos una garantía de claridad de 7 días. Si implementas el plan
            completo de 7 días y aún sientes que "no sabes qué decir", te
            reembolsaremos el 100% de tu compra. Para solicitar el reembolso:
          </p>
          <ul className="list-disc pl-6 space-y-2 my-4" data-id="element-226">
            <li data-id="element-227">Debes hacerlo dentro de los 7 días posteriores a la compra</li>
            <li data-id="element-228">
              Debes proporcionar evidencia de haber completado el plan de 7 días
            </li>
            <li data-id="element-229">
              Contáctanos a través de los canales proporcionados en tu compra
            </li>
          </ul>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-230">
            5. Descargo de Responsabilidad
          </h3>
          <p data-id="element-231">
            Este ebook proporciona información y estrategias, pero los
            resultados individuales pueden variar. No garantizamos resultados
            específicos de ventas o ingresos. El éxito depende de múltiples
            factores, incluyendo tu compromiso, contexto de negocio y aplicación
            del sistema.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-232">
            6. Propiedad Intelectual
          </h3>
          <p data-id="element-233">
            Todo el contenido del ebook, incluyendo textos, guiones, plantillas
            y diseño, está protegido por derechos de autor. Nos reservamos todos
            los derechos no expresamente otorgados en estos términos.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-234">
            7. Limitación de Responsabilidad
          </h3>
          <p data-id="element-235">
            No seremos responsables por daños indirectos, incidentales o
            consecuentes que surjan del uso o la imposibilidad de usar este
            producto.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-236">
            8. Modificaciones
          </h3>
          <p data-id="element-237">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Los cambios entrarán en vigor inmediatamente después de su
            publicación.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-238">
            9. Ley Aplicable
          </h3>
          <p data-id="element-239">
            Estos términos se rigen por las leyes aplicables en tu jurisdicción.
            Cualquier disputa se resolverá en los tribunales competentes.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mt-6 mb-3" data-id="element-240">
            10. Contacto
          </h3>
          <p data-id="element-241">
            Para preguntas sobre estos Términos y Condiciones, contáctanos a
            través de los canales proporcionados en tu compra.
          </p>
        </div>
      </Modal>
    </div>;
}